// .scss, not .css: see Fields/fileFiled/uploader.js — a plain .css import is
// never injected in the production bundle.
import './password.scss'

/**
 * Password field options (Pim, 27 Oct 2026), set per field in the form builder:
 *  - "Check strength": a meter under the field (weak / medium / strong) while it is
 *    typed; a weak password is invalid.
 *  - "Ask twice": a second box under the field, a copy of the field's own form-group
 *    (so it looks like every other field on that site), submitted as
 *    `<field>_confirmation`; a different value is invalid.
 *
 * Invalid = a custom validity error on the input, which is what submit gates read
 * (ExoForm's, and a site's own); a `change` is fired after every check so a gate that
 * listens there sees the new validity. Messages only show once the visitor has left
 * the box (never mid-typing). The server checks the same (Validations\PasswordField);
 * this is the courtesy, not the lock.
 */

// Keep in step with PasswordField::level().
const level = (password, minLength, minKinds) => {
    const kinds = [/\p{Ll}/u, /\p{Lu}/u, /\p{Nd}/u, /[^\p{L}\p{Nd}]/u].filter(re => re.test(password)).length
    if ([...password].length < minLength || kinds < minKinds) return 'weak'
    return [...password].length >= minLength + 4 && kinds >= 3 ? 'strong' : 'medium'
}

export default {
    init() {
        document.querySelectorAll('input[data-exo-password]').forEach(el => this.mount(el))
    },

    mount(input) {
        if (input.dataset.exoPasswordMounted || !input.name) return
        input.dataset.exoPasswordMounted = '1'

        let cfg = {}
        try { cfg = JSON.parse(input.dataset.exoPassword || '{}') } catch (e) { /* defaults */ }
        const L = cfg.labels || {}
        const minLength = cfg.minLength || 8
        const minKinds = cfg.minKinds || 2
        const group = input.closest('.form-group') || input.parentNode
        const inputRequired = input.required
        let after = group

        // ---- strength meter -----------------------------------------------------
        let meter = null
        if (cfg.strength) {
            meter = document.createElement('div')
            meter.className = 'exo-password__meter'
            meter.hidden = true
            meter.setAttribute('aria-live', 'polite')
            meter.innerHTML = '<div class="exo-password__bars" aria-hidden="true"><span></span><span></span><span></span></div>' +
                '<small class="exo-password__verdict"></small>'
            after.after(meter)
            after = meter
        }

        // ---- second box ---------------------------------------------------------
        let repeat = null
        let mismatch = null
        let matched = null
        if (cfg.confirm) {
            // A copy of the field's own wrapper; never of the form itself (a site without
            // form-groups), then a bare input is enough.
            const wrapper = group !== input.form && group.querySelectorAll('input:not([type=hidden])').length === 1
            const copy = wrapper ? group.cloneNode(true) : input.cloneNode(false)
            if (wrapper) copy.querySelectorAll('[data-aire-component="errors"], .invalid-feedback, .exo-password__meter').forEach(el => el.remove())
            repeat = wrapper ? copy.querySelector('input[name="' + input.name + '"]') : copy
            if (!wrapper && repeat) repeat.placeholder = L.repeat || 'Repeat password'
            if (repeat) {
                const id = (input.id || input.name) + '_confirmation'
                repeat.name = input.name + '_confirmation'
                repeat.id = id
                repeat.value = ''
                repeat.removeAttribute('data-exo-password')
                repeat.removeAttribute('data-exo-password-mounted')
                repeat.removeAttribute('data-aire-for')
                const label = copy.querySelector('label')
                if (label) {
                    label.setAttribute('for', id)
                    // The label's own words, wherever they sit (a site's label wraps them in a
                    // <span>), keeping a required mark ("*") as it is.
                    const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT)
                    let text = null
                    while (walker.nextNode()) {
                        const t = walker.currentNode.textContent.trim()
                        if (t !== '' && t !== '*') { text = walker.currentNode; break }
                    }
                    if (text) text.textContent = L.repeat || 'Repeat password'
                    else label.prepend(document.createTextNode((L.repeat || 'Repeat password') + ' '))
                }
                after.after(copy)
                after = copy
                // A green check at the end of the box once both match (Pim, 27 Oct) — the
                // mark the phone and passport fields show too.
                const holder = document.createElement('span')
                holder.className = 'exo-password__field'
                repeat.before(holder)
                holder.appendChild(repeat)
                matched = document.createElement('span')
                matched.className = 'exo-password__ok'
                matched.hidden = true
                matched.setAttribute('role', 'img')
                matched.setAttribute('aria-label', L.match || 'Passwords match')
                matched.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                holder.appendChild(matched)
                mismatch = document.createElement('small')
                mismatch.className = 'exo-password__mismatch'
                mismatch.hidden = true
                mismatch.setAttribute('aria-live', 'polite')
                mismatch.textContent = L.mismatch || 'The passwords do not match.'
                after.after(mismatch)
            }
        }

        // ---- validity -------------------------------------------------------
        let shownMain = false
        let shownRepeat = false
        const check = () => {
            const value = input.value
            if (meter) {
                const lvl = level(value, minLength, minKinds)
                const weak = value !== '' && lvl === 'weak'
                input.setCustomValidity(weak ? (L.rule || 'Too weak') : '')
                meter.hidden = value === ''
                meter.dataset.level = lvl
                meter.querySelector('.exo-password__verdict').textContent =
                    (L[lvl] || lvl) + (weak && shownMain ? ' · ' + (L.rule || '') : '')
            }
            if (repeat) {
                repeat.required = inputRequired || value !== ''
                const differs = repeat.value !== value && (value !== '' || repeat.value !== '')
                repeat.setCustomValidity(differs ? (L.mismatch || 'Mismatch') : '')
                mismatch.hidden = !(differs && shownRepeat)
                if (matched) matched.hidden = differs || value === ''
                repeat.parentNode.classList.toggle('exo-password__field--ok', !differs && value !== '')
                repeat.closest('.form-group')?.classList.toggle('exo-password--mismatch', differs && shownRepeat)
                repeat.dispatchEvent(new Event('change', { bubbles: true }))
            }
            // Let every submit gate see the new validity.
            input.dispatchEvent(new Event('change', { bubbles: true }))
        }

        input.addEventListener('input', check)
        input.addEventListener('blur', () => { if (input.value !== '') shownMain = true; check() })
        if (repeat) {
            repeat.addEventListener('input', () => {
                // Once it is as long as the first, a difference is worth saying at once.
                if (repeat.value.length >= input.value.length && input.value !== '') shownRepeat = true
                check()
            })
            repeat.addEventListener('blur', () => { if (repeat.value !== '') shownRepeat = true; check() })
        }
        check()
    },
}
